import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axios from "axios";
import { RefreshCw, ExternalLink, Loader2, Check, X, ListPlus, Lock, Unlock, Mail, RotateCcw, Wrench, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("narwhal_auth_token") || localStorage.getItem("token") || ""}`,
});

async function fetchConfig() {
  const r = await axios.get(`${API}/api/admin/reports-config`, { headers: authHeader() });
  return r.data;
}
async function fetchEmailTemplateAdmin() {
  const r = await axios.get(`${API}/api/admin/email-template`, { headers: authHeader() });
  return r.data;
}
async function saveEmailTemplateAdmin(body) {
  const r = await axios.put(`${API}/api/admin/email-template`, { body }, { headers: authHeader() });
  return r.data;
}
async function setSheet(clientId, url) {
  const r = await axios.put(
    `${API}/api/clients/${clientId}/dashboard-sheet`,
    { dashboard_sheet_id: url },
    { headers: authHeader() },
  );
  return r.data;
}
async function setBdSheet(clientId, url) {
  const r = await axios.put(
    `${API}/api/clients/${clientId}/bd-sheet`,
    { sheet_url: url },
    { headers: authHeader() },
  );
  return r.data;
}
async function setKickOff(clientId, date) {
  const r = await axios.put(
    `${API}/api/clients/${clientId}/dashboard-kick-off`,
    { dashboard_kick_off: date },
    { headers: authHeader() },
  );
  return r.data;
}
async function syncSheet(clientId, force = false) {
  const r = await axios.post(
    `${API}/api/admin/clients/${clientId}/dashboard-sync${force ? "?force=true" : ""}`,
    {},
    { headers: authHeader() },
  );
  return r.data;
}
async function seedRows(clientId) {
  const r = await axios.post(
    `${API}/api/admin/clients/${clientId}/dashboard-seed-rows`,
    {},
    { headers: authHeader() },
  );
  return r.data;
}
async function setSyncFlag(clientId, disabled) {
  const r = await axios.put(
    `${API}/api/clients/${clientId}/dashboard-sync-flag`,
    { disabled },
    { headers: authHeader() },
  );
  return r.data;
}
async function reseedClient(name) {
  const r = await axios.post(
    `${API}/api/admin/reseed-client`,
    { name },
    { headers: authHeader() },
  );
  return r.data;
}
async function deleteClient(clientId) {
  const r = await axios.delete(`${API}/api/admin/clients/${clientId}`, { headers: authHeader() });
  return r.data;
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function ReportsConfig() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["reports-config"], queryFn: fetchConfig });

  return (
    <div className="max-w-6xl mx-auto py-8 px-6" data-testid="reports-config">
      <div className="mb-8">
        <h1 className="font-display text-2xl font-semibold text-slate-900">Reportes · sincronización de planillas</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Gestioná las dos planillas de cada cliente: la <strong>Dashboard</strong> (&quot;Operación &amp; Métricas&quot;) que el
          app llena solo, y la <strong>BD Sheet</strong> con reuniones y briefs que usa el equipo comercial. Sincronizar solo escribe
          celdas vacías — la data manual histórica queda intacta.
        </p>
        <div className="mt-4 text-xs bg-slate-50 border border-slate-200 rounded-md px-4 py-3 text-slate-600 max-w-3xl">
          <div className="font-semibold text-slate-800 mb-1">Permisos requeridos</div>
          Compartí cada planilla como <span className="font-semibold">Editor</span> con:
          <div className="font-mono text-[11px] text-slate-800 mt-1">narwhal-dashboard@dashboards-emergent.iam.gserviceaccount.com</div>
        </div>
      </div>

      <EmailTemplateSection />

      {q.isLoading ? (
        <div className="py-16 text-center text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Cargando…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <th className="text-left py-3 px-4">Cliente</th>
                <th className="text-left py-3 px-4">Dashboard · Operación &amp; Métricas</th>
                <th className="text-left py-3 px-4">BD Sheet · Reuniones/Briefs</th>
                <th className="text-left py-3 px-4 whitespace-nowrap">Kick-off</th>
                <th className="text-left py-3 px-4 whitespace-nowrap">Última sync</th>
                <th className="text-right py-3 px-4">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.clients || []).map((c) => (
                <ClientRow key={c.id} client={c} onDone={() => qc.invalidateQueries({ queryKey: ["reports-config"] })} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SheetCell({ url, sheetId, placeholder, onSave, testidPrefix, clientId }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(url || sheetId || "");
  const save = useMutation({
    mutationFn: () => onSave(val),
    onSuccess: () => { toast.success("URL guardada"); setEditing(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar"),
  });
  const hasSheet = !!(url || sheetId);
  const displayUrl = url || (sheetId ? `https://docs.google.com/spreadsheets/d/${sheetId}/edit` : "");
  const displayLabel = sheetId || (url || "").replace(/^https?:\/\/docs\.google\.com\/spreadsheets\/d\//, "").split("/")[0] || url;

  if (editing) {
    return (
      <div className="flex items-center gap-2">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs font-mono min-w-[220px]"
          data-testid={`${testidPrefix}-input-${clientId}`}
        />
        <button onClick={() => save.mutate()} disabled={save.isPending} className="text-emerald-600 hover:text-emerald-700 p-1" data-testid={`${testidPrefix}-save-${clientId}`}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </button>
        <button onClick={() => { setEditing(false); setVal(url || sheetId || ""); }} className="text-slate-400 hover:text-slate-600 p-1">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }
  return hasSheet ? (
    <div className="flex items-center gap-2">
      <a href={displayUrl} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline font-mono truncate max-w-[220px] inline-flex items-center gap-1">
        <span className="truncate">{displayLabel}</span>
        <ExternalLink className="h-3 w-3 shrink-0" />
      </a>
      <button onClick={() => setEditing(true)} className="text-[11px] text-slate-500 hover:text-slate-800 underline">editar</button>
    </div>
  ) : (
    <button onClick={() => setEditing(true)} className="text-xs text-slate-500 hover:text-slate-900 italic" data-testid={`${testidPrefix}-add-${clientId}`}>
      + pegar URL
    </button>
  );
}

function ClientRow({ client, onDone }) {
  const [ko, setKo] = useState(client.dashboard_kick_off || "");

  const saveKo = useMutation({
    mutationFn: (date) => setKickOff(client.id, date),
    onSuccess: (r) => { toast.success(`Kick-off ${r.dashboard_kick_off}`); onDone(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Fecha inválida"),
  });
  const sync = useMutation({
    mutationFn: (force) => syncSheet(client.id, !!force),
    onSuccess: (r) => {
      const cw = r.cells_written ?? r.updated_cells ?? 0;
      const cs = r.cells_skipped_nonempty ?? 0;
      const mode = r.force ? "FORCE" : "safe";
      toast.success(
        `${client.name} [${mode}]: ${r.touched} filas · ${cw} celdas escritas${cs > 0 ? ` · ${cs} preservadas` : ""}`,
        { duration: 6000 },
      );
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync falló"),
  });
  const seed = useMutation({
    mutationFn: () => seedRows(client.id),
    onSuccess: (r) => {
      if (r.inserted > 0) {
        toast.success(`${client.name}: ${r.inserted} filas nuevas agregadas (${(r.labels || []).join(", ")})`);
      } else {
        toast.info(`${client.name}: ${r.reason || "todas las filas ya existen"}`);
      }
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Seed falló"),
  });
  const toggleFlag = useMutation({
    mutationFn: (disabled) => setSyncFlag(client.id, disabled),
    onSuccess: (r) => {
      toast.success(r.dashboard_sync_disabled ? `${client.name}: sync BLOQUEADO 🔒` : `${client.name}: sync habilitado`);
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo cambiar el flag"),
  });
  const repair = useMutation({
    mutationFn: () => reseedClient(client.name),
    onSuccess: (r) => {
      const parts = Object.entries(r.collections || {})
        .filter(([, v]) => (v.inserted + v.updated + (v.removed || 0)) > 0)
        .map(([k, v]) => {
          const bits = [];
          if (v.inserted) bits.push(`+${v.inserted}`);
          if (v.updated) bits.push(`~${v.updated}`);
          if (v.removed) bits.push(`-${v.removed}`);
          return `${k} ${bits.join(" ")}`;
        });
      toast.success(
        `${client.name}: reparado (${r.client}) · ${parts.join(" · ") || "nada que hacer"}`,
        { duration: 8000 },
      );
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reparación falló"),
  });
  const removeClient = useMutation({
    mutationFn: () => deleteClient(client.id),
    onSuccess: (r) => {
      toast.success(`${r.deleted_client} eliminado (con toda su data asociada)`, { duration: 6000 });
      onDone();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo eliminar"),
  });

  const hasSheet = !!client.dashboard_sheet_id;
  const isLocked = !!client.dashboard_sync_disabled;

  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
      <td className="py-3 px-4 font-medium text-slate-900 whitespace-nowrap">{client.name}</td>
      <td className="py-3 px-4">
        <SheetCell
          url=""
          sheetId={client.dashboard_sheet_id}
          placeholder="https://docs.google.com/spreadsheets/d/..."
          onSave={(v) => setSheet(client.id, v)}
          testidPrefix="rc-dash"
          clientId={client.id}
        />
      </td>
      <td className="py-3 px-4">
        <SheetCell
          url={client.bd_sheet_url}
          sheetId=""
          placeholder="URL de la planilla BD (reuniones/briefs)"
          onSave={(v) => setBdSheet(client.id, v)}
          testidPrefix="rc-bd"
          clientId={client.id}
        />
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">
        <input
          type="month"
          value={ko ? ko.slice(0, 7) : ""}
          onChange={(e) => { setKo(e.target.value); saveKo.mutate(e.target.value); }}
          className="border border-slate-200 rounded px-2 py-1 text-xs bg-white"
          data-testid={`rc-kickoff-${client.id}`}
        />
      </td>
      <td className="py-3 px-4 text-xs text-slate-500 whitespace-nowrap">{fmtDate(client.dashboard_last_sync)}</td>
      <td className="py-3 px-4 text-right whitespace-nowrap">
        <div className="inline-flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            disabled={repair.isPending}
            onClick={() => {
              if (window.confirm(`Reparar ${client.name}?\n\nEsto reaplica seed_data.json a este cliente: elimina duplicados en integrations (por platform), actualiza mappings de Metricool, BD y CRO. Usalo cuando la config no baja bien de preview a prod.`)) {
                repair.mutate();
              }
            }}
            className="gap-1.5 text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid={`rc-repair-${client.id}`}
            title="Reparar config prod (aplica seed + deduplica integrations)"
          >
            {repair.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />}
            Reparar
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={removeClient.isPending}
            onClick={() => {
              if (window.confirm(`ELIMINAR ${client.name}?\n\nEsto borra el cliente Y TODA su data asociada (integraciones, mappings, BD, publicaciones, reportes, SEO). No hay vuelta atrás.\n\n¿Seguro?`)) {
                removeClient.mutate();
              }
            }}
            className="gap-1.5 text-xs h-8 border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid={`rc-delete-${client.id}`}
            title="Eliminar cliente duplicado (borra toda su data)"
          >
            {removeClient.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="sm"
            variant={isLocked ? "default" : "ghost"}
            onClick={() => toggleFlag.mutate(!isLocked)}
            disabled={toggleFlag.isPending}
            className={`gap-1.5 text-xs h-8 ${isLocked ? "bg-rose-600 hover:bg-rose-700 text-white" : "text-slate-600 hover:text-slate-900"}`}
            data-testid={`rc-lock-${client.id}`}
            title={isLocked ? "Sync deshabilitado — click para habilitar" : "Sync habilitado — click para bloquear"}
          >
            {isLocked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!hasSheet || seed.isPending || isLocked}
            onClick={() => seed.mutate()}
            className="gap-1.5 text-xs h-8"
            data-testid={`rc-seed-${client.id}`}
            title={isLocked ? "Sync bloqueado" : "Insertar filas nuevas (Instagram, YouTube Video/Short, PPTs, Brochure, Diseño OFF, TikTok)"}
          >
            {seed.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListPlus className="h-3.5 w-3.5" />}
            Filas
          </Button>
          <Button
            size="sm"
            disabled={!hasSheet || sync.isPending || isLocked}
            onClick={() => sync.mutate(false)}
            className="gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs h-8"
            data-testid={`rc-sync-${client.id}`}
            title={isLocked ? "Sync bloqueado" : "Sync seguro: solo escribe celdas vacías o con 0"}
          >
            {sync.isPending && !sync.variables ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Sync
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!hasSheet || sync.isPending || isLocked}
            onClick={() => {
              if (window.confirm(`Force sync ${client.name}?\n\nEsto va a SOBREESCRIBIR todas las celdas de las métricas automatizadas (Metricool, Skylead, publicaciones por tipo, GSC, GA4).\n\nSolo úsalo si sabés que la data actual está mal.`)) {
                sync.mutate(true);
              }
            }}
            className="gap-1.5 text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid={`rc-force-${client.id}`}
            title={isLocked ? "Sync bloqueado" : "Force sync: sobreescribe TODO. Usar solo para limpiar data mala."}
          >
            {sync.isPending && sync.variables ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "⚠"}
            Force
          </Button>
        </div>
      </td>
    </tr>
  );
}


function EmailTemplateSection() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["email-template"], queryFn: fetchEmailTemplateAdmin });
  const [body, setBody] = useState(null);

  const currentValue = body ?? q.data?.body ?? "";
  const isDirty = body !== null && body !== q.data?.body;

  const save = useMutation({
    mutationFn: () => saveEmailTemplateAdmin(currentValue),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-template"] });
      setBody(null);
      toast.success("Template guardado");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo guardar"),
  });

  return (
    <div className="mb-8 bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="email-template-section">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
        <div>
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-600" /> Template global del email
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Cuerpo por defecto de los mails cuando enviás un reporte. Podés overridearlo por cliente (default_note) o inline al enviar.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setBody(q.data?.default || "")}
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 rounded"
            data-testid="tpl-reset-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Restablecer
          </button>
          <Button
            onClick={() => save.mutate()}
            disabled={!isDirty || save.isPending}
            className="gap-1.5 text-xs h-8 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
            data-testid="tpl-save-btn"
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Guardar
          </Button>
        </div>
      </div>
      <div className="p-5">
        {q.isLoading ? (
          <div className="text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Cargando…</div>
        ) : (
          <textarea
            value={currentValue}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder={q.data?.default}
            className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-slate-400 font-normal leading-relaxed"
            data-testid="tpl-body-textarea"
          />
        )}
        <div className="text-[11px] text-slate-500 mt-2">
          El botón &quot;Abrir reporte →&quot; se agrega automáticamente debajo del texto. Doble Enter = párrafo.
        </div>
      </div>
    </div>
  );
}
